"use client";

import React, { useMemo } from "react";
import type { EChartsOption } from "echarts";
import BaseChart, { type BaseChartRef } from "./base-chart";
import {
  chartTitle,
  chartTooltip,
  chartLegend,
  chartGrid,
  chartCategoryAxis,
  chartValueAxis,
  chartAnimation,
  chartColor,
  chartColors,
} from "./chart-utils";
import { cn } from "@/src/utils/utils";

/**
 * Convert a hex color (#rrggbb) to an rgba string with given alpha.
 * Falls back to the original color if not valid hex.
 */
function hexToRgba(hex: string, alpha: number): string {
  const match = hex.match(/^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/);
  if (match) {
    const r = parseInt(match[1], 16);
    const g = parseInt(match[2], 16);
    const b = parseInt(match[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  // Fallback: return as-is (shouldn't happen if resolveColor works)
  return hex;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AreaChartData {
  categories: string[];
  series: {
    name: string;
    data: number[];
    color?: string;
  }[];
}

export interface AreaChartProps {
  /** Data to display */
  data: AreaChartData;
  /** Chart title */
  title?: string;
  /** Chart subtitle */
  subtitle?: string;
  /** Chart height — CSS value or number (px). Default "100%" */
  height?: string | number;
  /** Extra class names for the wrapper */
  className?: string;
  /** Use smooth curves instead of straight lines. Default: true */
  smooth?: boolean;
  /** Stack areas on top of each other. Default: false */
  stacked?: boolean;
  /** Show legend. Default: true */
  showLegend?: boolean;
  /** Show loading spinner. Default: false */
  loading?: boolean;
  /** Show empty state. Default: false */
  empty?: boolean;
  /** Use gradient fill (0.4 opacity at top to transparent). Default: true */
  gradient?: boolean;
  /** Callback when a point is clicked */
  onPointClick?: (params: any) => void;
  /** Custom value formatter for tooltip */
  formatValue?: (value: number) => string;
  /** Accessible label for screen readers */
  ariaLabel?: string;
}

// ---------------------------------------------------------------------------
// AreaChart Component
// ---------------------------------------------------------------------------

export const AreaChart = React.forwardRef<BaseChartRef, AreaChartProps>(
  function AreaChart(
    {
      data,
      title,
      subtitle,
      height = "100%",
      className,
      smooth = true,
      stacked = false,
      showLegend = true,
      loading = false,
      empty = false,
      gradient = true,
      onPointClick,
      formatValue,
      ariaLabel,
    },
    ref
  ) {
    const option = useMemo(() => {
      const palette = chartColors();

      // Build series with area styling
      const series = data.series.map((s, idx) => {
        const seriesColor = s.color || chartColor(idx);
        const areaStyle = gradient
          ? {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: hexToRgba(seriesColor, 0.4) },
                  { offset: 1, color: hexToRgba(seriesColor, 0) },
                ],
                global: false,
              },
            }
          : { color: seriesColor };

        return {
          type: "line" as const,
          name: s.name,
          data: s.data,
          smooth,
          lineStyle: {
            width: 2.5,
            color: seriesColor,
          },
          areaStyle,
          symbol: "circle",
          symbolSize: 1,
          showSymbol: false,
          stack: stacked ? "total" : undefined,
          emphasis: {
            focus: "series" as const,
            blurScope: "coordinateSystem" as const,
            lineStyle: { width: 3.5 },
            itemStyle: {
              borderWidth: 3,
              borderColor: "#fff",
              shadowBlur: 6,
              shadowColor: "rgba(0,0,0,0.15)",
            },
          },
        };
      });

      const baseOption: Record<string, any> = {
        ...(title && chartTitle(title, { subtitle })),
        ...chartTooltip({
          trigger: "axis",
          formatter: (params: any) => {
            if (!Array.isArray(params) || params.length === 0) return "";
            const label = params[0].axisValue || "";
            const lines = [
              `<div style="margin: 0; padding: 0; font-weight: 600;">${label}</div>`,
            ];
            params.forEach((p: any) => {
              const value = formatValue ? formatValue(p.value) : p.value;
              lines.push(
                `<div style="margin: 4px 0 0 0; color: ${p.color};">● ${p.seriesName}: ${value}</div>`
              );
            });
            return lines.join("");
          },
        }),
        ...(showLegend && chartLegend()),
        ...chartGrid(),
        xAxis: chartCategoryAxis(data.categories),
        yAxis: chartValueAxis(),
        series,
      };

      return baseOption;
    }, [
      data,
      title,
      subtitle,
      smooth,
      stacked,
      showLegend,
      gradient,
      formatValue,
    ]);

    const onEvents = useMemo(
      () => ({
        ...(onPointClick && { click: onPointClick }),
      }),
      [onPointClick]
    );

    // Generate data summary for accessibility
    const dataSummary = useMemo(() => {
      const { categories, series } = data;
      const lines = series.map(s => {
        const points = s.data.map((val, i) => `${categories[i]}: ${formatValue ? formatValue(val) : val}`).join(", ");
        return `${s.name}: ${points}`;
      });
      return lines.join(". ");
    }, [data, formatValue]);

    return (
      <BaseChart
        ref={ref}
        option={option}
        height={height}
        className={cn("area-chart", className)}
        loading={loading}
        empty={empty}
        onEvents={Object.keys(onEvents).length > 0 ? onEvents : undefined}
        animationOptions={{
          delay: (idx: number) => idx * 60,
        }}
        ariaLabel={ariaLabel || `Area chart${title ? `: ${title}` : ""}`}
        dataSummary={dataSummary}
      />
    );
  }
);

export default AreaChart;
